import { FC } from 'react';

import { RoutePath } from '@common/constants';
import { Redirect, useRoute } from '@core/navigation';
import { useTranslation } from '@wearehelpful/ui-localization';
import {
  Content,
  Typography,
  Form,
  useUtils,
  Footer,
} from '@wearehelpful/ui-kit';
import { useBootstrapData } from '@bootstrap/hooks';
import { BDTO, ContentfulDocument } from '@helpful/network';

import { DetailsForm, RouteSearch } from './Details.types';
import { useDetails } from './Details.hooks';

export const Details: FC = () => {
  const { t } = useTranslation();
  const { toast } = useUtils();
  const { hints } = useBootstrapData();
  const [{ search }] = useRoute<object, RouteSearch>();

  const hint = hints.find(
    ({ type }) =>
      type === BDTO.HintItemType['why-do-we-need-this-info-sign-up-details']
  );

  const [{ defaultValues, isLoading, isSuccess }, update] = useDetails(search);

  const handleOnSubmit = (form: DetailsForm) => {
    update(form).catch((error) => {
      toast({ variant: 'error', message: error.message });
    });
  };

  if (isSuccess) {
    return <Redirect path={RoutePath.ROOT_MORE_INSURANCE} />;
  }

  return (
    <Form<DetailsForm> onSubmit={handleOnSubmit} defaultValues={defaultValues}>
      <Content>
        <Content.Subheader>
          <Typography variant="h2">
            {t('subheadings.insuranceConnectDetails.title')}
          </Typography>
        </Content.Subheader>
        <Content.Fixed>
          <Form.Text
            tabIndex={3}
            autocapitalize="on"
            name="firstName"
            validation={{ required: true }}
            label={t('forms.lovedOnesFirstNameShort')}
            help={t('hints.asItAppearsOnInsurance')}
          />
          <Form.Text
            tabIndex={4}
            autocapitalize="on"
            name="lastName"
            validation={{ required: true }}
            label={t('forms.lovedOnesLastNameShort')}
            help={t('hints.asItAppearsOnInsurance')}
          />
          {!!hint && (
            <Form.Hint
              title={hint.title}
              message={
                <ContentfulDocument>
                  {hint.longDescription.json}
                </ContentfulDocument>
              }
            >
              {hint.shortDescription}
            </Form.Hint>
          )}
        </Content.Fixed>
      </Content>
      <Footer>
        <Form.Submit checkValidity loading={isLoading}>
          {t('actions.done')}
        </Form.Submit>
      </Footer>
    </Form>
  );
};
